/**
 * Unidades del contrato que no calzan con lo que dicen describir.
 *
 * El contrato se transcribió a mano alguna vez y arrastra unidades que no
 * corresponden: la cuota mensual de radios y los operadores de apoyo figuran
 * en metros cúbicos. Nadie mide operadores en m³.
 *
 * Mientras esas partidas se carguen a mano da lo mismo —la cantidad va en la
 * unidad de la partida, sea cual sea la etiqueta—, pero en cuanto alguien
 * intente cargarlas por guía el sistema le va a exigir metros cúbicos. Y si
 * mañana se agrega una partida en sacos declarada en m³, el cobro sale mal
 * por un factor de cuarenta: eso ya pasó con el mortero T-100.
 *
 * Por eso se revisa, pero **no se corrige solo**. Una unidad define cómo se
 * cobra, y cambiarla sin que nadie mire es exactamente el tipo de decisión
 * que no debe tomar una máquina. El sistema detecta y propone; la persona
 * confirma.
 */

import type { ItemContrato, UnidadItem } from './tipos';
import { kgPorSaco } from './unidades';

export interface SospechaUnidad {
  codigo: string;
  descripcion: string;
  /** La que trae el contrato. */
  actual: UnidadItem;
  /** La que se deduce de la descripción. */
  sugerida: UnidadItem;
  /** Por qué, en una frase que se pueda leer en pantalla. */
  motivo: string;
}

type Item = Pick<ItemContrato, 'codigo' | 'descripcion' | 'unidad'>;

/** Unidades que sí miden volumen. Todo lo demás en m³ es sospechoso. */
const ES_VOLUMEN = (u: UnidadItem) => u === 'M3' || u === 'UF/M3';

interface Regla {
  /** Reconoce el tipo de partida por su descripción. */
  reconoce: (d: string, item: Item) => boolean;
  /** A qué unidad debería ir. */
  sugerida: UnidadItem;
  /** Si la unidad actual ya es una de éstas, no hay nada que corregir. */
  aceptables: UnidadItem[];
  motivo: string;
}

const REGLAS: Regla[] = [
  {
    /**
     * «Cuota/Mes corresponde a 7 Radios P25…»
     *
     * Exige la palabra «cuota» o «mensual». Un «/mes» suelto no basta: la
     * partida 15.2 dice «minimo transportado 380 m3/mes» y se cobra por metro
     * cúbico, no por mes.
     */
    reconoce: (d) => /cuota\s*\/?\s*mes|\bmensual\b/.test(d),
    sugerida: 'MES',
    aceptables: ['MES'],
    motivo: 'La descripción habla de una cuota mensual.',
  },
  {
    // «2 Operadores Apoyo Planta Hormigon Interior Mina»
    reconoce: (d) => /\boperador|\bsupervisor|\bpersonal\b|\bjornal/.test(d),
    sugerida: 'MES',
    aceptables: ['MES', 'UND', 'UF/DIA'],
    motivo: 'Es dotación de personal, no un material que se mida en volumen.',
  },
  {
    // «Mortero T-100 saco 25 kilos». El caso que costó el cobro por 40.
    reconoce: (_d, i) => kgPorSaco(i.descripcion) !== null,
    sugerida: 'TON',
    aceptables: ['TON', 'UF/KG', 'UF/SACO'],
    motivo: 'Se vende en sacos con peso declarado: se cobra por masa, no por volumen.',
  },
  {
    // «Arriendo camion mixer en faena», declarado con la unidad «B».
    reconoce: (d) => /\barriendo\b/.test(d),
    sugerida: 'MES',
    aceptables: ['MES', 'UF/DIA', 'UND', 'GL'],
    motivo: 'Un arriendo se cobra por tiempo, no por volumen.',
  },
  {
    // «Movilizacion», «Desmovilizacion»: pagos globales de una vez.
    reconoce: (d) => /\bmovilizacion|\btraslado a faena/.test(d),
    sugerida: 'GL',
    aceptables: ['GL', 'UND'],
    motivo: 'Es un pago global, no una cantidad que se mida.',
  },
];

/** Quita tildes y baja a minúsculas, para que las reglas no dependan de eso. */
const normalizar = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Revisa una partida. Devuelve la sospecha, o `null` si la unidad calza.
 *
 * Deliberadamente conservador: sólo señala cuando la descripción dice algo
 * concreto que contradice la unidad. Ante la duda, calla.
 */
export function revisarUnidad(item: Item): SospechaUnidad | null {
  const d = normalizar(item.descripcion);

  for (const r of REGLAS) {
    if (!r.reconoce(d, item)) continue;
    if (r.aceptables.includes(item.unidad)) return null;
    // Sólo se avisa si además la unidad actual mide volumen o es desconocida:
    // una partida por unidad donde esperábamos mes no es un error de cobro.
    if (!ES_VOLUMEN(item.unidad) && item.unidad !== 'B') return null;
    return {
      codigo: item.codigo,
      descripcion: item.descripcion,
      actual: item.unidad,
      sugerida: r.sugerida,
      motivo: r.motivo,
    };
  }

  // «B» no es una unidad del contrato: quedó de una transcripción.
  if (item.unidad === 'B') {
    return {
      codigo: item.codigo,
      descripcion: item.descripcion,
      actual: item.unidad,
      sugerida: 'UND',
      motivo: 'La unidad «B» no corresponde a ninguna del contrato.',
    };
  }

  return null;
}

/** Todas las partidas cuya unidad no calza con su descripción. */
export function revisarUnidades(items: Item[]): SospechaUnidad[] {
  return items
    .map(revisarUnidad)
    .filter((s): s is SospechaUnidad => s !== null)
    .sort((a, b) => a.codigo.localeCompare(b.codigo, 'es', { numeric: true, sensitivity: 'base' }));
}
